import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ExperienceAnchorType,
  IItemTemplateTimingDataChange,
  IItemTemplateTimingFixed,
  IItemTemplateTimingTriggered,
  ITaskTemplate,
  ITimelineItem
} from '@dadohr/shared/domainTypes/experience';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import styled from '@emotion/styled';
import {
  Button,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  Select,
  Typography
} from '@material-ui/core';
import { sortBy } from 'lodash';
import { ChevronDown, ExternalLink, Eye, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { WithShape } from '../../../../../../components/Charts/Util';
import { DatePickerReadonlyInput } from '../../../../../../components/DatePickerReadonlyInput';
import { TableSearch } from '../../../../../../components/Table';
import { useDialogState } from '../../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { COLOR_UNKNOWN } from '../../../../../../services/color';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  displayDaysToFixedStart,
  FixedTimingAnchorMode,
  fixedStartToDisplayDays,
  getAvailableAnchorModes,
  getDisplayDaysBounds,
  getEffectiveAnchorMode,
  getFixedDateForStart,
  getMilestoneOffsetDays,
  getStartForFixedDate,
  IItemTemplateTimingFixedWithAnchorMode
} from '../../../../../../services/experiences/anchorMode';
import { HUMAN_DATE_SHORT, ONE_DAY } from '../../../../../../services/time';
import { getResolvedTriggersForTimelineItem } from '../../../../../../services/experiences/template';
import {
  toSearchTermRegexp,
  useUserRuleConfigWithAutocompletion
} from '../../../../../../services/user';
import { getItemDisplayName } from '../../../../helpers/adminName';
import {
  ActorDescriptor,
  useActorDescriptorsFromTemplate
} from '../../../Actors';
import { getAnchorFields, useMilestoneShortLabel } from '../../../Anchors';
import { EMPTY_RULE_GROUP, RuleBuilder } from '../../../RuleBuilder';
import { TaskBuilderContext } from '../types';
import { AudienceMatchDialog } from './Audiences';
import { DaysModeRow, DaySelector, TimePicker } from './TimeSelectors';
import * as UX from '../../newux/kit';
import { NewUxButton } from '../../newux/kit/Button';

type TimingValue =
  | IItemTemplateTimingFixed
  | IItemTemplateTimingTriggered
  | IItemTemplateTimingDataChange;

type WhenMode =
  | 'afterTask'
  | 'anchorDateChanges'
  | 'conditionsMet'
  | 'fieldChanges';

const getWhenMode = (
  value: IItemTemplateTimingTriggered | IItemTemplateTimingDataChange,
  anchorField: string | null
): WhenMode => {
  if (value.type === 'triggered') return 'afterTask';
  if (value.type === 'dataChange' && value.watchField != null) {
    if (anchorField && value.watchField === anchorField)
      return 'anchorDateChanges';
    return 'fieldChanges';
  }
  return 'conditionsMet';
};

const PopoverContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(0.5),
  marginBottom: p.theme.spacing(0.5),
  padding: p.theme.spacing(1)
}));

const SpacedOutRow = styled('div')<{ border?: boolean }>((p) => ({
  borderBottom: p.border ? p.theme.custom.border.standard : undefined,
  paddingBottom: p.theme.spacing(0.5),
  marginBottom: p.theme.spacing(0.5)
}));

const RuleBuilderContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const TimelineTriggerSelectorItem = ({
  d,
  actorDescriptors
}: {
  d: ITaskTemplate;
  actorDescriptors: { [key: string]: ActorDescriptor };
}) => {
  return (
    <WithShape
      shape="square"
      color={actorDescriptors[d.actor]?.color || COLOR_UNKNOWN}
      ellipsis
      containerStyle={{ width: 'calc(100% - 20px)' }} // that is the size of the arrow icon + its padding
    >
      {getItemDisplayName(d)}
    </WithShape>
  );
};

export const TimlineTriggerSelector = ({
  value,
  onChange,
  options,
  actorDescriptors
}: {
  value: ITaskTemplate | null;
  onChange: (nextValue: ITaskTemplate) => void;
  options: ITaskTemplate[]; // sorting to be done outside, for now we also throw out other effect items
  actorDescriptors: { [key: string]: ActorDescriptor };
}) => {
  const { t } = useUnsafeTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const [q, setQ] = useState('');
  const filteredOptions = useMemo(() => {
    if (!q) {
      return options;
    }
    const re = toSearchTermRegexp(q);
    return options.filter((o) => getItemDisplayName(o).match(re));
  }, [q, options]);
  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ChevronDown size={16} />}
        onClick={() => setOpen((x) => !x)}
        ref={ref}
        // White fill so it reads as a field inside the selected radio card's blue tint.
        style={{ justifyContent: 'space-between', background: '#fff' }}
        fullWidth
      >
        {value ? (
          <TimelineTriggerSelectorItem
            d={value}
            actorDescriptors={actorDescriptors}
          />
        ) : (
          t('Select task')
        )}
      </Button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <PopoverContainer>
          <SpacedOutRow>
            <TableSearch
              term={q}
              onChange={setQ}
              autoFocus
              placeholder={t('Search by title')}
            />
          </SpacedOutRow>
        </PopoverContainer>
        {!!filteredOptions.length &&
          filteredOptions.map((o) => (
            <MenuItem
              key={o.id}
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
            >
              <TimelineTriggerSelectorItem
                d={o}
                actorDescriptors={actorDescriptors}
              />
            </MenuItem>
          ))}
        {!filteredOptions.length && (
          <MenuItem>
            <Typography variant="caption">{t('No tasks found')}</Typography>
          </MenuItem>
        )}
      </Popover>
    </>
  );
};

const WhenFieldChangesConfig = ({
  value,
  onChange,
  tplContext
}: {
  value: IItemTemplateTimingDataChange;
  onChange: (nextValue: TimingValue) => void;
  tplContext: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const [q, setQ] = useState('');

  const config = useUserRuleConfigWithAutocompletion(
    tplContext.orgId,
    org.customUserFields,
    tplContext.tz,
    {
      orgId: tplContext.orgId,
      experienceTemplateId: tplContext.templateId
    }
  );

  const fieldOptions = useMemo(
    () =>
      sortBy(
        Object.values(config).map((c) => ({ key: c.key, label: c.label })),
        (f) => f.label
      ),
    [config]
  );

  const filteredOptions = useMemo(() => {
    if (!q) return fieldOptions;
    const re = toSearchTermRegexp(q);
    return fieldOptions.filter((f) => f.label.match(re));
  }, [q, fieldOptions]);

  const selectedLabel = fieldOptions.find((f) => f.key === value.watchField)?.label;

  return (
    <FlexContainer
      direction="column"
      spacing={1}
      alignItems="flex-start"
      fullWidth
      // RadioCard's card is itself a <button> — without this, any click in here
      // bubbles up to the card's onSelect, which resets watchField back to ''.
      onClick={(e) => e.stopPropagation()}
    >
      <FlexContainer spacing={0.5} alignItems="center" fullWidth>
        <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
          {t('On or after day')}
        </Typography>
        <DaySelector
          value={value.earliestStart ?? 0}
          onChange={(nextStart) =>
            onChange({
              ...value,
              earliestStart: nextStart
            })
          }
          convertToWeeks={false}
          suffix={
            <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
              {t('of the experience')}
            </Typography>
          }
          mode="absolute"
        />
      </FlexContainer>
      <Button
        variant="outlined"
        endIcon={<ChevronDown size={16} />}
        onClick={() => setOpen((x) => !x)}
        ref={ref}
        // White fill so it reads as a field inside the selected radio card's blue tint.
        style={{ justifyContent: 'space-between', background: '#fff' }}
        fullWidth
      >
        {selectedLabel ?? 'Select field'}
      </Button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <PopoverContainer>
          <SpacedOutRow>
            <TableSearch
              term={q}
              onChange={setQ}
              autoFocus
              placeholder={t('Search by field')}
            />
          </SpacedOutRow>
        </PopoverContainer>
        {!!filteredOptions.length &&
          filteredOptions.map((f) => (
            <MenuItem
              key={f.key}
              onClick={() => {
                onChange({ ...value, watchField: f.key });
                setOpen(false);
              }}
            >
              {f.label}
            </MenuItem>
          ))}
        {!filteredOptions.length && (
          <MenuItem>
            <Typography variant="caption">{t('No fields found')}</Typography>
          </MenuItem>
        )}
      </Popover>
    </FlexContainer>
  );
};

const WhenConditionsMetConfig = ({
  value,
  onChange,
  tplContext
}: {
  value: IItemTemplateTimingDataChange;
  onChange: (nextValue: TimingValue) => void;
  tplContext: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const previewDialog = useDialogState();
  const config = useUserRuleConfigWithAutocompletion(
    tplContext.orgId,
    org.customUserFields,
    tplContext.tz,
    {
      orgId: tplContext.orgId,
      experienceTemplateId: tplContext.templateId
    }
  );

  return (
    <FlexContainer
      direction="column"
      spacing={1}
      alignItems="flex-start"
      fullWidth
      // RadioCard's card is itself a <button> — without this, any click in here
      // (including inside RuleBuilder) bubbles up to the card's onSelect, which
      // resets rules back to an empty group.
      onClick={(e) => e.stopPropagation()}
    >
      <FlexContainer spacing={0.5} alignItems="center" fullWidth>
        <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
          {t('On or after day')}
        </Typography>
        <DaySelector
          value={value.earliestStart ?? 0}
          onChange={(nextStart) =>
            onChange({
              ...value,
              earliestStart: nextStart
            })
          }
          suffix={
            <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
              {t('of the experience')}
            </Typography>
          }
          mode="absolute"
        />
      </FlexContainer>

      <RuleBuilderContainer>
        <RuleBuilder
          value={value.rules ?? null}
          onChange={(nextRule: Rule<string> | null) => {
            onChange({
              ...value,
              rules: nextRule
            });
          }}
          config={config}
          variant="compact"
          nullable
          showEmptyRowWhenNull={false}
        />
        <Button
          onClick={previewDialog.openDialog}
          startIcon={<Eye size={16} />}
          color="primary"
          style={{ alignSelf: 'flex-end' }}
        >
          {t('Preview')}
        </Button>
        <AudienceMatchDialog
          orgId={tplContext.orgId}
          open={previewDialog.dialogOpen}
          onClose={previewDialog.closeDialog}
          rule={value.rules ?? EMPTY_RULE_GROUP}
        />
      </RuleBuilderContainer>
    </FlexContainer>
  );
};

/**
 * NEW_UX day field for a fixed-time item on a milestone experience:
 * [number] days [after experience start | before milestone | after milestone].
 * Only `start` (plus the chosen mode) is stored; the number is derived.
 */
const MilestoneDaysField = ({
  value,
  onChange,
  offsetDays,
  milestoneLabel
}: {
  value: IItemTemplateTimingFixedWithAnchorMode;
  onChange: (nextValue: TimingValue) => void;
  offsetDays: number;
  milestoneLabel: string;
}) => {
  const { t } = useUnsafeTranslation();
  const mode = getEffectiveAnchorMode(value, offsetDays);
  const displayDays = fixedStartToDisplayDays(value.start, mode, offsetDays);
  const bounds = getDisplayDaysBounds(mode, offsetDays);

  const write = (days: number, nextMode: FixedTimingAnchorMode) => {
    const next: IItemTemplateTimingFixedWithAnchorMode = {
      ...value,
      start: displayDaysToFixedStart(days, nextMode, offsetDays),
      anchorMode: nextMode
    };
    onChange(next);
  };

  const error =
    value.start >= 0
      ? null
      : mode === 'beforeAnchor'
      ? t(
          "Can't be more than {{max}} days, because the experience starts {{max}} days before {{milestone}}",
          { max: bounds.max, milestone: milestoneLabel }
        )
      : t(
          "Can't be fewer than {{min}} days, because the experience starts {{min}} days after {{milestone}}",
          { min: bounds.min, milestone: milestoneLabel }
        );

  const modeLabels: Record<FixedTimingAnchorMode, string> = {
    expStart: t('after experience start'),
    beforeAnchor: t('before {{milestone}}', { milestone: milestoneLabel }),
    afterAnchor: t('after {{milestone}}', { milestone: milestoneLabel })
  };

  return (
    <DaysModeRow
      days={displayDays}
      mode={mode}
      options={getAvailableAnchorModes(offsetDays).map((m) => ({
        value: m,
        label: modeLabels[m]
      }))}
      min={bounds.min}
      max={bounds.max}
      error={error}
      onDaysChange={(days) => write(days, mode)}
      // Keep the number the user is looking at; the item moves. An
      // out-of-range result shows its error straight away.
      onModeChange={(nextMode, days) =>
        write(
          Math.max(days, getDisplayDaysBounds(nextMode, offsetDays).min),
          nextMode
        )
      }
    />
  );
};

/** NEW_UX day field for a fixed-time item on a fixed-date experience: a date picker. */
const FixedDateField = ({
  value,
  onChange,
  anchorTs,
  tz
}: {
  value: IItemTemplateTimingFixed;
  onChange: (nextValue: TimingValue) => void;
  anchorTs: Timestamp;
  tz: string;
}) => {
  const { t } = useUnsafeTranslation();
  const days = Math.round(value.start / ONE_DAY);
  return (
    <div>
      <DatePickerReadonlyInput
        value={getFixedDateForStart(anchorTs, value.start, tz)}
        minDate={getFixedDateForStart(anchorTs, 0, tz)}
        format={HUMAN_DATE_SHORT}
        inputVariant="outlined"
        variant="inline"
        autoOk
        style={{ width: 180, background: '#fff' }}
        onChange={(m) => {
          if (m && m.isValid()) {
            onChange({
              ...value,
              start: getStartForFixedDate(anchorTs, m, tz)
            });
          }
        }}
      />
      <UX.FieldHint>
        {days === 0
          ? t('On experience start date')
          : days === 1
          ? t('1 day after experience starts')
          : t('{{days}} days after experience starts', { days })}
      </UX.FieldHint>
    </div>
  );
};

/** Picks the NEW_UX fixed-time day control for the experience's start setting. */
const FixedTimingFields = ({
  value,
  onChange,
  tplContext
}: {
  value: IItemTemplateTimingFixed;
  onChange: (nextValue: TimingValue) => void;
  tplContext: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const { anchor, tz } = tplContext;
  const milestoneLabel = useMilestoneShortLabel(anchor);
  const offsetDays = getMilestoneOffsetDays(anchor);

  if (anchor.type === ExperienceAnchorType.FIXED) {
    return (
      <FixedDateField
        value={value}
        onChange={onChange}
        anchorTs={anchor.ts}
        tz={tz}
      />
    );
  }
  if (offsetDays !== null) {
    return (
      <MilestoneDaysField
        value={value}
        onChange={onChange}
        offsetDays={offsetDays}
        milestoneLabel={milestoneLabel ?? t('milestone')}
      />
    );
  }
  return (
    <FlexContainer spacing={0.5} alignItems="center" fullWidth>
      <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
        {t('Day')}
      </Typography>
      <DaySelector
        value={value.start}
        onChange={(nextStart) => onChange({ ...value, start: nextStart })}
        suffix={
          <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
            {t('of experience')}
          </Typography>
        }
        mode="absolute"
        convertToWeeks={false}
      />
    </FlexContainer>
  );
};

export const TimelineItemTiming = ({
  value,
  onChange,
  tplContext,
  onGoToOtherItem,
  optionIdsToIgnore = EMPTY_ARR,
  newUx = false
}: {
  value: TimingValue;
  onChange: (nextValue: TimingValue) => void;
  tplContext: TaskBuilderContext;
  onGoToOtherItem?: (item: ITimelineItem) => void;
  optionIdsToIgnore?: string[]; // this is for tasks, which cannot select themselves
  // NEW_UX: render trigger modes as kit radio-cards (same modes/handlers/configs).
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  // NEW_UX: last value seen for each timing mode while this panel is open, so
  // switching cards and back restores what was there instead of a blank value.
  const modeStashRef = useRef<Partial<Record<'fixed' | WhenMode, TimingValue>>>(
    {}
  );
  const actorDescriptors = useActorDescriptorsFromTemplate(
    tplContext.supportingActors
  );
  const allTasks = tplContext.tasks;
  const triggers = useMemo(
    () => getResolvedTriggersForTimelineItem(value, allTasks),
    [value, allTasks]
  );

  const options = useMemo(() => {
    const ids = triggers.map((tr) => tr.itemId);
    return sortBy(
      allTasks.filter(
        (ta) => !ids.includes(ta.id) && !optionIdsToIgnore.includes(ta.id)
      ),
      (ta) => getItemDisplayName(ta)
    );
  }, [triggers, allTasks, optionIdsToIgnore]);

  const { org } = useCurrentUser();
  const anchorField =
    tplContext.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE
      ? tplContext.anchor.field
      : null;
  const anchorName = useMemo(() => {
    if (!anchorField) return null;
    const fields = getAnchorFields(org.customUserFields);
    const field = fields.find((f) => f.field === anchorField);
    return field?.shortLabel(t) || anchorField;
  }, [anchorField, org.customUserFields, t]);

  const isWhenMode = value.type === 'triggered' || value.type === 'dataChange';
  const whenMode: WhenMode = isWhenMode
    ? getWhenMode(value, anchorField)
    : 'conditionsMet';

  const handleWhenModeChange = (nextMode: WhenMode) => {
    if (nextMode === 'afterTask') {
      onChange({
        type: 'triggered',
        dependentOn: []
      });
    } else if (nextMode === 'anchorDateChanges') {
      onChange({
        type: 'dataChange',
        watchField: anchorField,
        rules: null,
        earliestStart: 0,
        time: null
      });
    } else if (nextMode === 'fieldChanges') {
      onChange({
        type: 'dataChange',
        watchField: '',
        rules: null,
        earliestStart: 0,
        time: null
      });
    } else {
      onChange({
        type: 'dataChange',
        watchField: null,
        rules: { ...EMPTY_RULE_GROUP },
        earliestStart: 0,
        time: null
      });
    }
  };

  if (newUx) {
    const currentMode = value.type === 'fixed' ? 'fixed' : whenMode;
    const selectMode = (m: 'fixed' | WhenMode) => {
      // Re-clicking the already-selected card (e.g. its own background, not a
      // child control) must be a no-op — otherwise it unconditionally rebuilds
      // a fresh value (e.g. wiping an in-progress rule back to an empty group)
      // even though nothing actually changed.
      if (m === currentMode) return;
      modeStashRef.current[currentMode] = value;
      const stashed = modeStashRef.current[m];
      if (stashed) {
        onChange(stashed);
      } else if (m === 'fixed') {
        onChange({ type: 'fixed', start: 0, time: null });
      } else {
        handleWhenModeChange(m);
      }
    };
    const modeCards: { id: 'fixed' | WhenMode; title: string; desc: string }[] = [
      { id: 'fixed', title: t('At a fixed time'), desc: t('Sends on a set day') },
      { id: 'conditionsMet', title: t('When conditions met'), desc: t('Matches participant profile data') },
      { id: 'afterTask', title: t('After a task is completed'), desc: t('Chain off another timeline item') },
      ...(anchorField
        ? [{ id: 'anchorDateChanges' as WhenMode, title: t('{{anchorDate}} changes', { anchorDate: anchorName }), desc: t('When the anchor date changes') }]
        : []),
      { id: 'fieldChanges', title: t('When a data field changes'), desc: t('Triggered by changes to participant profile data') }
    ];
    return (
      <div style={{ width: '100%' }}>
        {modeCards.map((c) => (
          <UX.RadioCard
            key={c.id}
            selected={currentMode === c.id}
            title={c.title}
            description={c.desc}
            onSelect={() => selectMode(c.id)}
          >
            {c.id === 'fixed' && value.type === 'fixed' && (
              <FlexContainer
                direction="column"
                spacing={0.5}
                alignItems="flex-start"
                fullWidth
                // RadioCard's card has its own onSelect handler — without this, any
                // click in here (DaySelector, Add Time, the time field, its clear
                // button) bubbles up to the card's onSelect, which resets time back
                // to null.
                onClick={(e) => e.stopPropagation()}
              >
                <FixedTimingFields
                  value={value}
                  onChange={onChange}
                  tplContext={tplContext}
                />
                {!value.time && (
                  <NewUxButton
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<Plus size={14} />}
                    onClick={() => onChange({ ...value, time: { hour: 9, minute: 0 } })}
                    // Local alignment fix only — the field above has no left padding, so
                    // offset this button's own left padding to line up with it.
                    style={{ marginLeft: -12 }}
                  >
                    {t('Add Time')}
                  </NewUxButton>
                )}
                {value.time && (
                  <TimePicker
                    t={value.time}
                    minuteStep={10}
                    onChange={(nextTime) => onChange({ ...value, time: nextTime })}
                    onClear={() => onChange({ ...value, time: null })}
                    tz={tplContext.tz}
                    width={145}
                  />
                )}
              </FlexContainer>
            )}
            {c.id === 'afterTask' && value.type === 'triggered' && (
              // RadioCard's card is itself a <button> — without this, any click in
              // here bubbles up to the card's onSelect, which resets dependentOn back
              // to an empty array.
              <div onClick={(e) => e.stopPropagation()}>
                <TimlineTriggerSelector
                  value={triggers[0]?.item || null}
                  onChange={(nextItem) =>
                    onChange({
                      ...value,
                      dependentOn: [{ itemType: 'TASK', itemId: nextItem.id, condition: 'COMPLETE' }]
                    })
                  }
                  options={options}
                  actorDescriptors={actorDescriptors}
                />
                {!!(onGoToOtherItem && triggers[0]?.item) && (
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    startIcon={<ExternalLink size={14} />}
                    onClick={() => onGoToOtherItem({ item: triggers[0].item, itemType: 'TASK' })}
                  >
                    {t('Edit task')}
                  </Button>
                )}
              </div>
            )}
            {c.id === 'fieldChanges' && value.type === 'dataChange' && (
              <WhenFieldChangesConfig value={value} onChange={onChange} tplContext={tplContext} />
            )}
            {c.id === 'conditionsMet' && value.type === 'dataChange' && (
              <WhenConditionsMetConfig value={value} onChange={onChange} tplContext={tplContext} />
            )}
          </UX.RadioCard>
        ))}
      </div>
    );
  }

  return (
    <FlexContainer
      direction="column"
      fullWidth
      alignItems="flex-start"
      spacing={1}
    >
      <FlexContainer
        direction="column"
        spacing={1}
        alignItems="flex-start"
        fullWidth
      >
        <FormControlLabel
          control={
            <Radio
              checked={value.type === 'fixed'}
              onChange={() =>
                onChange({
                  type: 'fixed',
                  start: 0,
                  time: null
                })
              }
              color="primary"
            />
          }
          label={t('Fixed day')}
        />
        {value.type === 'fixed' && (
          <FlexContainer
            direction="column"
            spacing={0.5}
            alignItems="flex-start"
            fullWidth
          >
            <DaySelector
              value={value.start}
              onChange={(nextStart) =>
                onChange({
                  ...value,
                  start: nextStart
                })
              }
              suffix={t('of experience')}
              mode="absolute"
              convertToWeeks={false}
            />

            {!value.time && (
              <Button
                color="primary"
                onClick={() => {
                  onChange({
                    ...value,
                    time: { hour: 9, minute: 0 }
                  });
                }}
                size="small"
              >
                {t('Add Time')}
              </Button>
            )}
            {value.time && (
              <TimePicker
                t={value.time}
                minuteStep={10}
                onChange={(nextTime) => onChange({ ...value, time: nextTime })}
                onClear={() => onChange({ ...value, time: null })}
              />
            )}
          </FlexContainer>
        )}
      </FlexContainer>

      <FlexContainer
        direction="column"
        spacing={1}
        alignItems="flex-start"
        fullWidth
      >
        <FormControlLabel
          control={
            <Radio
              checked={isWhenMode}
              onChange={() => handleWhenModeChange('conditionsMet')}
              color="primary"
            />
          }
          label={t('When...')}
        />
        {isWhenMode && (
          <FlexContainer
            direction="column"
            spacing={1}
            alignItems="flex-start"
            fullWidth
          >
            <Select
              value={whenMode}
              onChange={(e) =>
                handleWhenModeChange(e.target.value as WhenMode)
              }
              variant="outlined"
              fullWidth
              style={{ height: 40 }}
            >
              <MenuItem value="afterTask">
                {t('another task is completed')}
              </MenuItem>
              {anchorField && (
                <MenuItem value="anchorDateChanges">
                  {t('{{anchorDate}} changes', {
                    anchorDate: anchorName
                  })}
                </MenuItem>
              )}
              <MenuItem value="fieldChanges">
                {t('a data field changes')}
              </MenuItem>
              <MenuItem value="conditionsMet">
                {t('the following conditions are met')}
              </MenuItem>
            </Select>

            {whenMode === 'afterTask' && value.type === 'triggered' && (
              <>
                <TimlineTriggerSelector
                  value={triggers[0]?.item || null}
                  onChange={(nextItem) =>
                    onChange({
                      ...value,
                      dependentOn: [
                        {
                          itemType: 'TASK',
                          itemId: nextItem.id,
                          condition: 'COMPLETE'
                        }
                      ]
                    })
                  }
                  options={options}
                  actorDescriptors={actorDescriptors}
                />
                {!!(onGoToOtherItem && triggers[0]?.item) && (
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    startIcon={<ExternalLink size={14} />}
                    onClick={() =>
                      onGoToOtherItem({
                        item: triggers[0].item,
                        itemType: 'TASK'
                      })
                    }
                  >
                    {t('Edit task')}
                  </Button>
                )}
              </>
            )}

            {whenMode === 'fieldChanges' &&
              value.type === 'dataChange' && (
                <WhenFieldChangesConfig
                  value={value}
                  onChange={onChange}
                  tplContext={tplContext}
                />
              )}

            {whenMode === 'conditionsMet' &&
              value.type === 'dataChange' && (
                <WhenConditionsMetConfig
                  value={value}
                  onChange={onChange}
                  tplContext={tplContext}
                />
              )}
          </FlexContainer>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};
